import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import {
  stripFirebaseClientConfig,
  initSwFirebase,
  isIosDevice,
  isStandalonePwa,
  pushSupported,
} from '../utils/pushFirebase';

export default function usePushEnrollment(lang = 'ar') {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.getPushStatus();
      setStatus(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const iosHint = isIosDevice() && !isStandalonePwa()
    ? (lang === 'ar'
      ? 'على iPhone افتح التطبيق من أيقونة الشاشة الرئيسية'
      : 'On iPhone open from Home Screen icon')
    : null;

  async function enable() {
    if (!pushSupported()) {
      setError(lang === 'ar' ? 'غير مدعوم' : 'Not supported');
      return false;
    }
    setLoading(true);
    setError('');
    try {
      if (iosHint) throw new Error(iosHint);
      const rawConfig = await api.getFirebaseConfig();
      const { appConfig, vapidKey } = stripFirebaseClientConfig(rawConfig);
      if (!vapidKey) throw new Error(lang === 'ar' ? 'مفتاح Push مفقود' : 'Missing key');
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error(lang === 'ar' ? 'تم رفض الإذن' : 'Permission denied');
      }
      const { initializeApp, getApps } = await import('firebase/app');
      const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
      if (!(await isSupported())) throw new Error(lang === 'ar' ? 'غير مدعوم' : 'Not supported');
      if (!navigator.serviceWorker.controller) {
        await navigator.serviceWorker.register('/service-worker.js');
      }
      const reg = await initSwFirebase(rawConfig);
      await new Promise((r) => setTimeout(r, 400));
      const app = getApps().length ? getApps()[0] : initializeApp(appConfig);
      const messaging = getMessaging(app);
      const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: reg });
      if (!token) throw new Error(lang === 'ar' ? 'تعذر التسجيل' : 'Registration failed');
      await api.registerFcm({
        token,
        platform: 'web',
        device_label: navigator.userAgent.slice(0, 120),
      });
      sessionStorage.removeItem('push_banner_dismiss');
      await loadStatus();
      return true;
    } catch (e) {
      setError(e.message || (lang === 'ar' ? 'فشل التفعيل' : 'Failed'));
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function disable() {
    setLoading(true);
    setError('');
    try {
      try {
        const rawConfig = await api.getFirebaseConfig();
        const { appConfig } = stripFirebaseClientConfig(rawConfig);
        const { initializeApp, getApps } = await import('firebase/app');
        const { getMessaging, getToken, deleteToken } = await import('firebase/messaging');
        const app = getApps().length ? getApps()[0] : initializeApp(appConfig);
        const messaging = getMessaging(app);
        const token = await getToken(messaging).catch(() => null);
        if (token) {
          await api.unregisterFcm({ token });
          await deleteToken(messaging);
        }
      } catch { /* optional cleanup */ }
      await loadStatus();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function setEnabled(on) {
    if (on) return enable();
    return disable();
  }

  return {
    status,
    loading,
    error,
    subscribed: !!status?.subscribed,
    available: pushSupported() && status?.enabled !== false && status?.plan_allows !== false,
    iosHint,
    setEnabled,
    reload: loadStatus,
  };
}